import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Check, Loader2, Mail, Phone, Plus, Trash2, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";

function ytId(url) {
  if (!url) return null;
  const s = url.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/,
  );
  return m ? m[1] : null;
}

function VideosPanel() {
  const [videos, setVideos] = useState([]);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyOrder, setBusyOrder] = useState(false);

  const load = () => api.get("/admin/videography/videos").then((r) => setVideos(r.data));
  useEffect(() => {
    load().catch(() => {});
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!ytId(url)) return toast.error("Paste a valid YouTube URL or 11-char video ID");
    setSaving(true);
    try {
      await api.post("/admin/videography/videos", { url: url.trim(), title: title.trim() });
      setUrl("");
      setTitle("");
      await load();
      toast.success("YouTube video added to the showcase");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const commitOrder = async (next) => {
    setVideos(next);
    setBusyOrder(true);
    try {
      await api.patch("/admin/videography/videos/order", { ids: next.map((v) => v.id) });
    } catch {
      toast.error("Couldn't save order — refreshing");
      load();
    } finally {
      setBusyOrder(false);
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= videos.length) return;
    const next = [...videos];
    [next[idx], next[target]] = [next[target], next[idx]];
    commitOrder(next);
  };

  const remove = async (vid) => {
    if (!window.confirm("Remove this video from the Videography page?")) return;
    await api.delete(`/admin/videography/videos/${vid}`);
    load();
  };

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <form
        onSubmit={add}
        className="space-y-3 rounded-2xl border border-white/10 bg-[#141419] p-5 lg:col-span-2"
        data-testid="admin-videography-video-form"
      >
        <h3 className="font-heading font-bold text-white">Add YouTube Video</h3>
        <input
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=…"
          className={inputCls}
          data-testid="admin-videography-url-input"
        />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional — pulls from YouTube if left blank)"
          className={inputCls}
          data-testid="admin-videography-title-input"
        />
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-videography-add-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-3 text-xs font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          Add to Showcase
        </button>
        <p className="text-[11px] text-zinc-600">
          These embed on /videography. Drop a watch URL, short link, or an 11-char ID.
        </p>
      </form>

      <div className="space-y-3 lg:col-span-3" data-testid="admin-videography-videos-list">
        {videos.length > 0 && (
          <p className="flex items-center gap-2 text-[11px] text-zinc-500">
            Top row shows first on /videography.
            {busyOrder && <Loader2 size={11} className="animate-spin text-emerald-300" />}
          </p>
        )}
        {videos.map((v, idx) => (
          <div
            key={v.id}
            className={`flex items-start gap-3 rounded-2xl border p-3 ${
              idx === 0 ? "border-emerald-300/50 bg-emerald-300/5" : "border-white/10 bg-[#141419]"
            }`}
            data-testid={`admin-videography-row-${v.id}`}
          >
            <img
              src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`}
              alt=""
              className="h-14 w-24 shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">{v.title || v.youtube_id}</p>
              <p className="truncate font-mono text-[11px] text-zinc-500">{v.youtube_id}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0 || busyOrder}
                data-testid={`admin-videography-up-${v.id}`}
                aria-label="Move up"
                className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowUp size={13} />
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === videos.length - 1 || busyOrder}
                data-testid={`admin-videography-down-${v.id}`}
                aria-label="Move down"
                className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowDown size={13} />
              </button>
              <button
                onClick={() => remove(v.id)}
                data-testid={`admin-videography-delete-${v.id}`}
                aria-label="Delete"
                className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        {videos.length === 0 && (
          <p className="rounded-2xl border border-dashed border-white/10 p-6 text-center text-sm text-zinc-600">
            No videos yet — add your first YouTube release above.
          </p>
        )}
      </div>
    </div>
  );
}

function IntakesPanel() {
  const [intakes, setIntakes] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/admin/videography/intakes").then((r) => setIntakes(r.data));
  useEffect(() => {
    load().catch(() => {});
  }, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/videography/intakes/${id}/status`, { status });
    setIntakes((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this videography enquiry?")) return;
    await api.delete(`/admin/videography/intakes/${id}`);
    setIntakes((rows) => rows.filter((r) => r.id !== id));
  };

  return (
    <div className="space-y-3" data-testid="admin-videography-intakes">
      {intakes.map((r) => {
        const open = openId === r.id;
        return (
          <div
            key={r.id}
            className="rounded-2xl border border-white/10 bg-[#141419] p-4"
            data-testid={`admin-videography-intake-${r.id.slice(0, 8)}`}
          >
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white">
                  {r.name}
                  <span className="ml-2 rounded-full bg-emerald-300/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-200">
                    {r.service_type}
                  </span>
                  {r.status === "new" && (
                    <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-300">
                      New
                    </span>
                  )}
                </p>
                <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1">
                    <Mail size={11} /> {r.email}
                  </span>
                  {r.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone size={11} /> {r.phone}
                    </span>
                  )}
                  <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">
                    {(r.created_at || "").slice(0, 10)}
                  </span>
                </p>
              </div>
              <select
                value={r.status}
                onChange={(e) => setStatus(r.id, e.target.value)}
                data-testid={`admin-videography-status-${r.id.slice(0, 8)}`}
                className="rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2 text-xs text-white"
              >
                {["new", "contacted", "booked", "closed"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : r.id)}
                data-testid={`admin-videography-expand-${r.id.slice(0, 8)}`}
                className="rounded-full border border-white/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10"
              >
                {open ? "Hide" : "View"}
              </button>
              <button
                type="button"
                onClick={() => remove(r.id)}
                aria-label="Delete"
                className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 size={12} />
              </button>
            </div>
            {open && (
              <div className="mt-3 space-y-2 border-t border-white/10 pt-3 text-xs text-zinc-400">
                {r.project_title && (
                  <p>
                    <span className="font-mono uppercase text-zinc-500">Project · </span>
                    <span className="text-white">{r.project_title}</span>
                  </p>
                )}
                <p className="whitespace-pre-wrap">
                  <span className="font-mono uppercase text-zinc-500">Brief · </span>
                  {r.description}
                </p>
                {r.references && (
                  <p className="whitespace-pre-wrap">
                    <span className="font-mono uppercase text-zinc-500">References · </span>
                    {r.references}
                  </p>
                )}
                {(r.budget || r.deadline || r.location) && (
                  <p className="flex flex-wrap gap-x-4 text-zinc-500">
                    {r.budget && <span>Budget: <span className="text-emerald-200">{r.budget}</span></span>}
                    {r.deadline && <span>Deadline: <span className="text-white">{r.deadline}</span></span>}
                    {r.location && <span>Location: <span className="text-white">{r.location}</span></span>}
                  </p>
                )}
                {r.notes && (
                  <p className="whitespace-pre-wrap text-zinc-500">
                    <span className="font-mono uppercase">Notes · </span>
                    {r.notes}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
      {intakes.length === 0 && (
        <p className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-600">
          No videography enquiries yet.
        </p>
      )}
    </div>
  );
}

export function VideographyTab() {
  const [panel, setPanel] = useState("intakes");
  return (
    <div className="space-y-6" data-testid="admin-videography-tab">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setPanel("intakes")}
          data-testid="admin-videography-panel-intakes"
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
            panel === "intakes" ? "bg-emerald-300 text-black" : "border border-white/10 text-zinc-400 hover:text-white"
          }`}
        >
          <Check size={12} /> Enquiries
        </button>
        <button
          type="button"
          onClick={() => setPanel("videos")}
          data-testid="admin-videography-panel-videos"
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
            panel === "videos" ? "bg-emerald-300 text-black" : "border border-white/10 text-zinc-400 hover:text-white"
          }`}
        >
          <Youtube size={12} /> Released Videos
        </button>
      </div>
      {panel === "intakes" ? <IntakesPanel /> : <VideosPanel />}
    </div>
  );
}
